import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import './App.css';

function App() {
  const [euroAmount, setEuroAmount] = useState(() => {
    // Load saved tour price from cookies on initial load, default to 1730
    const savedAmount = Cookies.get('tourPrice');
    return savedAmount || '1730';
  });
  const [exchangeRate, setExchangeRate] = useState(() => {
    // Load cached exchange rate from cookies
    const cachedRate = Cookies.get('exchangeRate');
    return cachedRate ? parseFloat(cachedRate) : null;
  });
  const [uahAmount, setUahAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  // Fetch exchange rate from Monobank API with smart caching
  useEffect(() => {
    const fetchExchangeRate = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check cached rate and timestamp
        const cachedRate = Cookies.get('exchangeRate');
        const cachedTimestamp = Cookies.get('exchangeRateTimestamp');
        const now = Date.now();

        // If we have cached data
        if (cachedRate && cachedTimestamp) {
          const cacheAge = now - parseInt(cachedTimestamp);
          const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds
          const twentyFourHours = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

          // If cache is less than 5 minutes old, use it without fetching
          if (cacheAge < fiveMinutes) {
            setExchangeRate(parseFloat(cachedRate));
            setLoading(false);
            return;
          }

          // Cache is older than 5 minutes, try to fetch fresh data
          try {
            const response = await fetch('https://api.monobank.ua/bank/currency');
            const data = await response.json();
            
            // Find EUR to UAH rate (currencyCodeA: 978 (EUR), currencyCodeB: 980 (UAH))
            const eurToUah = data.find(rate => 
              rate.currencyCodeA === 978 && rate.currencyCodeB === 980
            );
            
            if (eurToUah && eurToUah.rateSell) {
              // Save fresh rate and timestamp to cookies
              const newRate = eurToUah.rateSell;
              Cookies.set('exchangeRate', newRate.toString(), { expires: 7 });
              Cookies.set('exchangeRateTimestamp', now.toString(), { expires: 7 });
              setExchangeRate(newRate);
            } else {
              throw new Error('Exchange rate not found in response');
            }
          } catch (fetchErr) {
            console.error('Error fetching fresh exchange rate:', fetchErr);
            
            // Fetch failed, check if cached data is still acceptable
            if (cacheAge < twentyFourHours) {
              // Use cached rate if it's less than 24 hours old
              setExchangeRate(parseFloat(cachedRate));
              setError('Используется кэшированный курс (проблемы с сетью)');
            } else {
              // Cached rate is too old, show error
              throw new Error('Курс валют устарел и не удается обновить');
            }
          }
        } else {
          // No cached data, must fetch fresh
          const response = await fetch('https://api.monobank.ua/bank/currency');
          const data = await response.json();
          
          // Find EUR to UAH rate
          const eurToUah = data.find(rate => 
            rate.currencyCodeA === 978 && rate.currencyCodeB === 980
          );
          
          if (eurToUah && eurToUah.rateSell) {
            // Save fresh rate and timestamp to cookies
            const newRate = eurToUah.rateSell;
            Cookies.set('exchangeRate', newRate.toString(), { expires: 7 });
            Cookies.set('exchangeRateTimestamp', now.toString(), { expires: 7 });
            setExchangeRate(newRate);
          } else {
            throw new Error('Exchange rate not found');
          }
        }
      } catch (err) {
        setError('Ошибка загрузки курса валют');
        console.error('Error with exchange rate:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchExchangeRate();
  }, []);

  // Save tour price to cookies whenever it changes
  useEffect(() => {
    if (euroAmount) {
      // Save with 30 days expiration
      Cookies.set('tourPrice', euroAmount, { expires: 30 });
    } else {
      // Remove cookie if input is empty
      Cookies.remove('tourPrice');
    }
  }, [euroAmount]);

  // Calculate UAH amount when euro amount or exchange rate changes
  useEffect(() => {
    if (euroAmount && exchangeRate) {
      const amount = parseFloat(euroAmount);
      if (!isNaN(amount)) {
        setUahAmount(amount * exchangeRate);
      } else {
        setUahAmount(0);
      }
    } else {
      setUahAmount(0);
    }
  }, [euroAmount, exchangeRate]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    // Allow only numbers and decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setEuroAmount(value);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('uk-UA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const copyToClipboard = async () => {
    try {
      const textToCopy = formatCurrency(uahAmount);
      await navigator.clipboard.writeText(textToCopy);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const forceRefreshRate = () => {
    // Clear cached exchange rate data and force refresh
    Cookies.remove('exchangeRate');
    Cookies.remove('exchangeRateTimestamp');
    window.location.reload();
  };

  return (
    <div className="App">
      <div className="container">
        <div className="converter-card">
          <h1 className="title">Конвертер валют</h1>
          <p className="subtitle">Рассчитай стоимость тура в гривнах</p>
          
          {loading && (
            <div className="loading">
              <div className="spinner"></div>
              <p>Загрузка курса валют...</p>
            </div>
          )}

          {error && (
            <div className={error.includes('кэшированный') ? "warning" : "error"}>
              <p>{error}</p>
              {error.includes('кэшированный') ? (
                <button onClick={forceRefreshRate} className="refresh-btn">
                  🔄 Обновить курс
                </button>
              ) : (
                <button onClick={() => window.location.reload()} className="retry-btn">
                  Попробовать снова
                </button>
              )}
            </div>
          )}

          {!loading && (!error || error.includes('кэшированный')) && (
            <>
              <div className="input-section">
                <label htmlFor="euro-input" className="input-label">
                  ✈️ Стоимость тура в евро
                </label>
                <div className="input-container">
                  <input
                    id="euro-input"
                    type="text"
                    value={euroAmount}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    className="euro-input"
                    autoComplete="off"
                  />
                  <span className="currency-symbol">€</span>
                </div>
              </div>

              <div className="exchange-rate-info">
                <p>Курс: 1 € = {formatCurrency(exchangeRate)} ₴</p>
                {(() => {
                  const timestamp = Cookies.get('exchangeRateTimestamp');
                  if (timestamp) {
                    const lastUpdate = new Date(parseInt(timestamp));
                    const now = new Date();
                    const diffMinutes = Math.floor((now - lastUpdate) / (1000 * 60));
                    
                    if (diffMinutes < 1) {
                      return <small>Обновлено только что</small>;
                    } else if (diffMinutes < 60) {
                      return <small>Обновлено {diffMinutes} мин назад</small>;
                    } else {
                      const diffHours = Math.floor(diffMinutes / 60);
                      return <small>Обновлено {diffHours} ч назад</small>;
                    }
                  }
                  return null;
                })()}
              </div>

              <div className="result-section">
                <div className="result-label">Сумма в гривнаx</div>
                <div className="uah-amount">
                  {formatCurrency(uahAmount)} <span className="currency-symbol-uah">₴</span>
                </div>
                <button 
                  className="copy-btn" 
                  onClick={copyToClipboard}
                  disabled={!uahAmount}
                >
                  {copySuccess ? '✓ Скопировано!' : '📋 Копировать'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
